<template>
  <div>
    <notifications></notifications>
    <filter-menu
      v-if="showInfo"
      @closeFilters="closeInfo"
      title="About Dialler Monitor"
    >
      <InfoMenu />
    </filter-menu>

    <div class="loading" v-if="loadingLayout">
      <p class="loading-text">Loading layout</p>
      <div class="bouncer">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>

    <div v-if="!refreshToken">
      <ControlMenu
        @toggleLock="toggleLock"
        @toggleMenu="showMenu = !showMenu"
        @toggleMargins="toggleMargins"
        @openSelectLayout="openSelectLayout = !openSelectLayout"
        @openSave="saving = true"
        @changeConfigs="changeConfigs"
        :menuState="showMenu"
        :canEdit="canEdit"
      />
      <SideMenu
        v-if="showMenu"
        @closeMenu="showMenu = false"
        @added="addedItem"
      />
      <LayoutSelection
        v-if="openSelectLayout"
        @close="openSelectLayout = false"
        @loadLayout="changeLayout($event)"
      />

      <dashboard class="board" :id="'dash'">
        <dash-layout
          v-for="layout in dlayouts"
          v-bind="layout"
          :key="layout.breakpoint"
        >
          <dash-item
            v-for="(item, index) in layout.items"
            v-bind.sync="item"
            :key="item.id"
            :resizable="!locked"
            class="dash-cell"
            :class="{ 'drag-border': !locked, border: margins }"
            :draggable="!locked"
            @resizeEnd="toggleResize(index, true)"
          >
            <component
              :is="item.component"
              :config="item.config"
              :shouldResize="item.shouldResize"
              @resized="toggleResize(index, false)"
              @configChange="updateConfig(index, ...arguments)"
              @loaded="itemLoaded()"
              @delete="deleteItem(item.id)"
            />
          </dash-item>
        </dash-layout>
      </dashboard>
    </div>
    <div
      style="
        height: 100vh;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      "
      v-else
    >
      The token provided is invalid or has expired. If you are accessing via
      GDMS try reloading Dialler Monitor.
    </div>
    <SaveLayout v-if="saving" @close="saving = false" />
    <popup ref="confirm"></popup>
  </div>
</template>

<script>
import { Dashboard, DashLayout, DashItem } from "vue-responsive-dash";
import Toolbox from "@/toolbox";
import {
  SaveLayout,
  SideMenu,
  ControlMenu,
  LayoutSelection,
  InfoMenu,
} from "@/components";
import DiaMonD from "../../public/scripts/diamond-api.min.js";

export default {
  name: "DashboardContainer",
  components: {
    Dashboard,
    DashLayout,
    DashItem,
    SideMenu,
    ControlMenu,
    LayoutSelection,
    SaveLayout,
    InfoMenu,
  },

  data() {
    return {
      margins: true,
      showMenu: false,
      dlayouts: [],
      moving: -1,
      movingArgs: {},
      refreshToken: false,
      openSelectLayout: false,
      toolbox: [],
      saving: false,
      loadingLayout: true,
      showInfo: false,
      itemsLoaded: 0,
      canEdit: true,
    };
  },

  watch: {
    agentArgs: {
      deep: true,
      handler() {
        if (!this.loadingLayout) {
          this.$diamond.sendAgentParameters(this.$store.getters.agentArgs);
        }
      },
    },

    campaignArgs: {
      deep: true,
      handler() {
        if (!this.loadingLayout)
          this.$diamond.sendCampaignParameters(
            this.$store.getters.campaignArgs
          );
      },
    },

    queueArgs: {
      deep: true,
      handler() {
        if (!this.loadingLayout)
          this.$diamond.sendQueueParameters(this.$store.getters.queueArgs);
      },
    },
  },

  computed: {
    locked() {
      return (
        this.$store.getters.tempLock ||
        this.$store.getters.filterOpen ||
        this.$store.getters.locked
      );
    },

    items() {
      return this.$store.getters.controls;
    },

    agentArgs() {
      return this.$store.getters.agentArgs;
    },

    campaignArgs() {
      return this.$store.getters.campaignArgs;
    },

    queueArgs() {
      return this.$store.getters.queueArgs;
    },
  },

  async mounted() {
    window.addEventListener(
      "message",
      (e) => {
        if (e.data === "focused") {
          this.items.forEach((x) => {
            if (Object.prototype.hasOwnProperty.call(x, "shouldResize")) {
              this.$set(x, "shouldResize", true);
            }
          });
        }
      },
      false
    );

    this.toolbox = Toolbox;
    var tokenInfo = this.checkAndSetToken();
    var site = null;
    
    let diamond = new DiaMonD(tokenInfo.location, tokenInfo.token, 0);
    var tenantSite = await diamond.getSite();

    if (this.$route.query.siteId) site = parseInt(this.$route.query.siteId, 10);

    
    this.$connectToDiamond(tokenInfo.location, tokenInfo.token, site == null ? tenantSite.data : site, tenantSite.data != site);

    window.addEventListener("reconnected", this.sendData);

    this.$diamond
      .GetOpenConnection()
      .then(() => {
        this.$diamond
          .getData()
          .then(() => {
            this.$diamond.attachListeners(
              this.campaignInfoReceived,
              this.queueInfoReceived,
              this.agentInfoReceived
            );
            this.$diamond.attachUpdateListeners(
              this.campaignUpdateReceived,
              this.queueUpdateReceived,
              this.agentUpdateReceived
            );

            var cols = 16;
            var rows = 28;
            var minColWidth = window.innerWidth / cols - cols;

            this.dlayouts = [
              {
                breakpoint: "lg",
                numberOfCols: cols,
                rowHeight: window.innerHeight / rows,
                minColWidth: minColWidth,
              },
            ];

            this.changeLayout(-1);
          })
          .catch((err) => {
            console.log(err)
            if (localStorage.token) this.$router.push({ name: "Login" });
            else {
              this.loadingLayout = false;
              this.refreshToken = true;
            }
          });
      })
      .catch((err) => console.log(err));

    this._keyListener = function (e) {
      if (e.key === "i" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.showDebug();
      }
    };

    document.addEventListener("keydown", this._keyListener.bind(this));
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this._keyListener);
  },

  methods: {
    async deleteItem(id) {
      var confirm = await this.$refs.confirm.show({
        title: "Delete item & config",
        message: `Are you sure you want to remove this item?`,
        okButton: "Delete",
        isConfirm: true,
      });
      if (confirm) {
        var index = this.items.findIndex((x) => x.id == id);
        this.$store.commit("removeControl", index);
        this.showMenu = true;
        this.showMenu = false;
      }
    },

    closeInfo() {
      this.showInfo = false;
    },

    showDebug() {
      this.showInfo = true;
    },

    toggleLock() {
      this.$store.commit("setLockedState", !this.$store.getters.locked);
    },

    toggleResize(index, shouldResize) {
      this.$set(this.items[index], "shouldResize", shouldResize);
    },

    changeLayout(id) {
      this.loadingLayout = true;
      this.$store.commit("resetArgs");
      this.$store.commit("resetData");
      this.dlayouts[0].items = [];

      this.$diamond
        .getDashboardLayout(id)
        .then((res) => {
          if (res.data !== "") {
            this.canEdit = res.data.canEdit;
            var layout = res.data.layout;
            this.$store.commit("setCurrentLayout", layout.id);

            if (layout) {
              var data = JSON.parse(layout.data);
              var controls = [];

              var ref = JSON.parse(JSON.stringify(data.controls));

              for (let i = 0; i < data.controls.length; i++) {
                var index = this.toolbox.findIndex(
                  (x) => x.name == data.controls[i].component
                );
                var item = data.controls[i];
                item.component = { ...this.toolbox[index].component };
                controls.push(item);
              }

              this.$store.commit("setControls", controls);
              this.dlayouts[0].items = this.items;
              this.$store.commit("setBoardName", layout.name);

              this.$store.commit("setBoardCreator", layout.createdBy);

              //Properties added to kick start data
              this.$store.getters.agentArgs.addProperty("AgentName");
              this.$store.getters.campaignArgs.addProperty("CampaignName");
              this.$store.getters.queueArgs.addProperty("QueueNumber");

              this.$diamond.sendAgentParameters(this.agentArgs);
              this.$diamond.sendCampaignParameters(this.campaignArgs);
              this.$diamond.sendQueueParameters(this.queueArgs);

              this.showMenu = true;
              this.showMenu = false;

              this.loadingLayout = false;

              setTimeout(() => {
                for (let i = 0; i < ref.length; i++) {
                  var itemCheck = this.items[i];
                  if (itemCheck.x != ref[i].x || itemCheck.y != ref[i].y) {
                    this.$store.commit("setControlPosition", {
                      id: itemCheck.id,
                      x: ref[i].x,
                      y: ref[i].y,
                    });
                  }
                }
              }, 20);
            }
          } else {
            this.loadingLayout = false;
          }
        })
        .catch(() => {
          this.loadingLayout = false;
        });
    },

    addedItem() {
      this.showMenu = false;
      this.showMenu = true;
    },

    itemLoaded() {
      this.itemsLoaded += 1;
      if (this.itemsLoaded == this.items.length) this.loadingLayout = false;
    },

    toggleMargins() {
      this.margins = !this.margins;
      this.dlayouts[0].margin = this.margins
        ? { x: 10, y: 10 }
        : { x: 0, y: 0 };

      //Toggles menu to enforce margin change, happens faster than a render tick so not visible on front end
      this.showMenu = !this.showMenu;
      this.showMenu = !this.showMenu;
    },

    updateConfig(index, config) {
      this.items[index].config = JSON.parse(JSON.stringify(config));
    },

    campaignInfoReceived(infoArray) {
      this.$store.commit("setCampaignData", infoArray);
    },

    queueInfoReceived(infoArray) {
      this.$store.commit("setQueueData", infoArray);
    },

    agentInfoReceived(infoArray) {
      this.$store.commit("setAgentData", infoArray);
    },

    campaignUpdateReceived(campaignObject) {
      this.$store.commit("updateCampaignObject", campaignObject);
    },

    queueUpdateReceived(queueObject) {
      this.$store.commit("updateQueueObject", queueObject);
    },

    agentUpdateReceived(agentObject) {
      this.$store.commit("updateAgentObject", agentObject);
    },

    changeConfigs(propertyName, value) {
      for (let i = 0; i < this.items.length; i++) {
        if (
          Object.prototype.hasOwnProperty.call(
            this.items[i].config,
            propertyName
          )
        ) {
          var hasProp = Object.prototype.hasOwnProperty.call(
            this.items[i].config[propertyName],
            "locked"
          );
          if (hasProp && this.items[i].config[propertyName].locked) continue;
          this.items[i].config[propertyName] = value;
        }
      }
    },

    checkAndSetToken() {
      var token = "";
      var location = "";

      if (!localStorage.token && !this.$route.query.token)
        this.$router.push({ name: "Login" });

      if (localStorage.token) {
        token = localStorage.token;
        location = "wallboard";
      }

      if (this.$route.query.token) {
        token = this.$route.query.token;
        location = "dialler-monitor";
      }

      return {
        token: token,
        location: location,
      };
    },

    sendData() {
      this.$diamond.sendAgentParameters(this.agentArgs);
      this.$diamond.sendCampaignParameters(this.campaignArgs);
      this.$diamond.sendQueueParameters(this.queueArgs);
    },
  },
};
</script>

<style scoped>
.item {
  background-color: white;
}

.menu {
  position: absolute;
  width: 100%;
  background-color: rgb(235, 235, 235);
  z-index: 1;
  top: 20px;
  padding: 20px;
  box-sizing: border-box;
  overflow-x: auto;
  display: flex;
  align-items: center;
}

.content {
  height: 100%;
  width: 100%;
  border: 2px solid #42b983;
  border-radius: 5px;
}

.noselect {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.dash-cell {
  display: flex !important;
  justify-content: center;
  align-items: center;
  margin: 3px;
  background-color: white;
}

.drag-border {
  border: 1px dashed rgba(48, 48, 48, 0.4);
}

.delete-bar {
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #a8a8a8;
  color: white;
  font-size: 2em;
}

.loading {
  position: absolute;
  height: 100vh;
  width: 100vw;
  background-color: rgb(248, 248, 248);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.bouncer {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  width: 100px;
  height: 200px;
}

.bouncer div {
  width: 20px;
  height: 20px;
  background-color: #303030;
  border-radius: 50%;
  animation: bouncer 0.5s cubic-bezier(0.19, 0.57, 0.3, 0.98) infinite alternate;
}

.bouncer div:nth-child(2) {
  animation-delay: 0.1s;
  opacity: 0.8;
}

.bouncer div:nth-child(3) {
  animation-delay: 0.2s;
  opacity: 0.6;
}

.bouncer div:nth-child(4) {
  animation-delay: 0.3s;
  opacity: 0.4;
}

@keyframes bouncer {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100px);
  }
}

.loading-text {
  text-transform: uppercase;
  font-size: 32pt;
}

.border {
  border-radius: 10px;
}

.board {
  min-height: 100vh;
  min-width: 100%;
}
</style>